import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@material-ui/core';
import { fetchLearningPlanAggregateReport } from '../services/learning-plan.service';
import { PROGRESS_STATUS } from '../constants/learning-plans-constants';

const useLearningPlanProgressHook = (learningPlanKey) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  // Color utility functions
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case PROGRESS_STATUS.COMPLETED:
        return theme.status.completed;
      case PROGRESS_STATUS.IN_PROGRESS:
        return theme.status.inProgress;
      default:
        return theme.palette.greyScale.platinum;
    }
  }, [theme]);
  
  const [state, setState] = useState({
    learningPlan: null,
    pillarProgress: [],
    loading: true,
    error: null
  });

  const fetchProgressData = useCallback(async () => {
    if (!learningPlanKey) {
      navigate('/learning-plans');
      return;
    }

    // Get the plan object from navigation state
    const planFromState = location.state?.plan;
    
    if (!planFromState || !planFromState.id) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Learning plan information not found'
      }));
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Fetch aggregate report from API (data is already transformed in service)
      const reportData = await fetchLearningPlanAggregateReport(planFromState.id);
      
      // Pillars are already transformed by the service
      const pillarProgress = reportData?.pillars || [];

      // Calculate total learners from pillar data
      const totalLearners = pillarProgress.reduce((sum, pillar) => {
        return sum + pillar.completed + pillar.inProgress;
      }, 0);

      const learningPlan = {
        id: planFromState.id,
        title: planFromState.title || learningPlanKey,
        key: planFromState.key || learningPlanKey,
        reportId: `AGG-${reportData.learningPlanId || planFromState.id}-${(planFromState.key || learningPlanKey).toUpperCase()}`,
        totalLearners: totalLearners,
        learnerCount: pillarProgress.length,
      };

      setState({
        learningPlan,
        pillarProgress,
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('Error fetching learning plan progress:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch learning plan progress'
      }));
    }
  }, [learningPlanKey, location.state, navigate]);

  useEffect(() => {
    fetchProgressData();
  }, [fetchProgressData]);

  const handleBackToList = () => {
    navigate('/learning-plans');
  };

  const handleNavigateToDetails = () => {
    if (state.learningPlan && state.learningPlan.key) {
      navigate(`/learning-plans/${state.learningPlan.key}`, {
        state: { plan: state.learningPlan }
      });
    }
  };

  return {
    learningPlan: state.learningPlan,
    pillarProgress: state.pillarProgress,
    loading: state.loading,
    error: state.error,
    refreshProgressData: fetchProgressData,
    handleBackToList,
    handleNavigateToDetails,
    getStatusColor,
  };
};

export default useLearningPlanProgressHook;

